import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Model, Question, QuestionSignaturePadModel, setLicenseKey } from "survey-core";
import { Survey } from "survey-react-ui";
import { SurveyPDF } from "survey-pdf";
import { toast } from "sonner";
import "survey-core/survey-core.min.css";
import "./form-handler-survey.css";
import { patientFormTheme } from "./patientFormTheme";
import { printedDate } from "./printedDate";

import { getFacilityKey, getPatientKey } from "@/store/profile";
import patientRequest from "@/api/patient-request";
import taskRequest from "@/api/task-request";
import {
  getPortalForm,
  uploadPortalForm,
  updatePortalFormComplete,
} from "@/api/form-portal-request";
import {
  buildFormHandlerLaunchPayload,
  parseFacilityFormInfoFromTaskJson,
} from "@/types/portal-forms";
import { isFormTask, type TaskItem } from "@/types/tasks";
import {
  clearFormHandlerLaunch,
  formHandlerLaunchFromLegacySearchParams,
  navigateToFormHandler,
  normalizeFormHandlerLaunchState,
  readFormHandlerLaunch,
  writeFormHandlerLaunch,
} from "@/utils/formHandlerLaunch";

/** Last ~40 submit steps; survives `location.reload` and full navigations. Inspect: `JSON.parse(sessionStorage.getItem("formHandlerSubmitDebug") ?? "[]")` */
const FORM_HANDLER_SUBMIT_DEBUG_KEY = "formHandlerSubmitDebug";

function logFormHandlerSubmit(step: string, data: Record<string, unknown>): void {
  const payload = { t: new Date().toISOString(), step, ...data };
  console.info(`[FormHandler] ${step}`, data);
  try {
    const prev = sessionStorage.getItem(FORM_HANDLER_SUBMIT_DEBUG_KEY);
    const arr: object[] = prev ? (JSON.parse(prev) as object[]) : [];
    arr.push(payload);
    sessionStorage.setItem(
      FORM_HANDLER_SUBMIT_DEBUG_KEY,
      JSON.stringify(arr.slice(-40))
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function userFacingSubmitError(e: unknown): string {
  if (e instanceof Error) {
    const m = e.message.trim();
    if (m.length > 0) return m;
    return "Save failed with no message from the app. Open DevTools → Network, find UploadForm, and inspect the response body.";
  }
  if (typeof e === "string" && e.trim()) return e.trim();
  return "Something went wrong saving your form. Please try again.";
}

/**
 * SurveyJS `data` as JSON for the portal API, with `patientSignature` set when the form uses
 * a signaturepad under another name (backend often expects `patientSignature`).
 */
function buildFormCompleteDataJson(sender: Model): string {
  const out: Record<string, unknown> = { ...sender.data };
  const hasPatientSig =
    out.patientSignature != null && String(out.patientSignature).trim().length > 0;
  if (!hasPatientSig) {
    for (const q of sender.getAllQuestions()) {
      if (q.getType() === "signaturepad" && !q.isEmpty()) {
        const v = q.value;
        if (v != null && String(v).trim().length > 0) {
          out.patientSignature = v;
          break;
        }
      }
    }
  }
  return JSON.stringify(out);
}

async function surveyModelToBase64Pdf(model: Model): Promise<string> {
  const surveyPDF = new SurveyPDF(model.toJSON(), {
    fontSize: 12,
    compress: true,
    htmlRenderAs: "image",
  });
  surveyPDF.data = model.data;

  surveyPDF.getAllQuestions().forEach((q: Question) => {
    if (q.getType() === "text" && q.inputType === "date" && !q.isEmpty()) {
      q.value = printedDate(q.value) ?? q.value;
    }
  });

  const dataUrl = await surveyPDF.raw("dataurlstring");
  const parts = dataUrl.split("base64,");
  const base64 = parts.length > 1 ? parts[1] : "";
  if (!base64) throw new Error("Could not generate PDF.");
  return base64;
}

export function FormHandler() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const legacySearchKey = useMemo(() => searchParams.toString(), [searchParams]);
  const navigate = useNavigate();
  const facilityKey = getFacilityKey();
  const patientKey = getPatientKey();

  const [loadError, setLoadError] = useState<string | null>(null);
  const [model, setModel] = useState<Model | null>(null);
  const [phase, setPhase] = useState<"loading" | "survey" | "submitting">("loading");

  const modelRef = useRef<Model | null>(null);
  const submittingLock = useRef(false);

  /** Title / labels for the survey chrome (set when launch context is valid). */
  const [launchUi, setLaunchUi] = useState<{
    formTitleHint: string;
    patientLabel: string;
    formCountHint: number;
  } | null>(null);

  useEffect(() => {
    const key = import.meta.env.VITE_APP_SURVEY_JS_KEY;
    if (key) setLicenseKey(key);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const disposeModel = () => {
      const m = modelRef.current;
      if (m) {
        try {
          m.dispose();
        } catch {
          /* ignore */
        }
        modelRef.current = null;
      }
      setModel(null);
    };

    disposeModel();
    setLoadError(null);
    setLaunchUi(null);
    setPhase("loading");
    submittingLock.current = false;

    if (!facilityKey || !patientKey) {
      setLoadError("Your session is missing facility or patient information. Please sign in again.");
      return;
    }

    const launch =
      normalizeFormHandlerLaunchState(location.state) ?? readFormHandlerLaunch();

    if (!launch) {
      const legacy = formHandlerLaunchFromLegacySearchParams(searchParams);
      if (legacy) {
        writeFormHandlerLaunch(legacy);
        navigate("/form-handler", { replace: true, state: legacy });
        return;
      }
    }

    if (!launch) {
      clearFormHandlerLaunch();
      setLoadError("This form link is invalid.");
      return;
    }

    writeFormHandlerLaunch(launch);
    const { facilityFormId, taskId, formTitleHint, patientLabel, formCountHint } = launch;
    setLaunchUi({ formTitleHint, patientLabel, formCountHint });

    (async () => {
      try {
        const [form, tasksRes] = await Promise.all([
          getPortalForm(facilityKey, patientKey, facilityFormId),
          patientRequest.request.gettasks(facilityKey, patientKey),
        ]);
        if (cancelled) return;

        if (form.error) {
          setLoadError(form.error);
          return;
        }
        const raw = form.data?.trim();
        if (!raw) {
          setLoadError("This form has no definition.");
          return;
        }

        const schema = JSON.parse(raw) as Record<string, unknown>;
        // A PDF form (WEB-1715) can't be signed in the portal yet (WEB-1724); the task stays open (WEB-1808).
        if (schema.formKind === "pdf") {
          setLoadError("This form needs to be signed at the clinic.");
          return;
        }
        const m = new Model(schema);
        m.applyTheme(patientFormTheme);
        // Signature pads default to a fixed 300px canvas; scale them to the column width on phones.
        for (const q of m.getAllQuestions()) {
          if (q instanceof QuestionSignaturePadModel) q.signatureAutoScaleEnabled = true;
        }
        m.clearInvisibleValues = false;
        m.completedHtml =
          '<span style="font-size: 32px; font-weight: bolder">Thank you for completing the form</span>';

        const tasks = ((tasksRes?.data as TaskItem[]) ?? []).filter((t) =>
          isFormTask(t.taskType)
        );
        const task = tasks.find((t) => String(t.taskID) === taskId);
        if (task?.info) {
          const info = parseFacilityFormInfoFromTaskJson(task.info);
          if (info?.Data && Object.keys(info.Data).length > 0) {
            m.data = { ...m.data, ...info.Data } as typeof m.data;
          }
        }

        const onComplete = async (sender: Model) => {
          if (submittingLock.current) return;
          submittingLock.current = true;
          setPhase("submitting");
          try {
            const base64 = await surveyModelToBase64Pdf(sender);
            logFormHandlerSubmit("pdfGenerated", {
              facilityFormId,
              taskId,
              base64LengthChars: base64.length,
            });

            const upload = await uploadPortalForm(
              facilityKey,
              patientKey,
              facilityFormId,
              base64
            );
            logFormHandlerSubmit("uploadFormResponse", {
              success: upload.success,
              id: upload.id,
              reason: upload.reason,
            });
            if (!upload.success) {
              const detail = upload.reason?.trim();
              throw new Error(
                detail
                  ? `PDF upload was not accepted: ${detail}`
                  : "PDF upload was not accepted. The API returned success: false with an empty reason — check server logs, permissions, or PDF/base64 size limits."
              );
            }

            const formDataJson = buildFormCompleteDataJson(sender);
            logFormHandlerSubmit("updateFormCompletePayload", {
              dataLengthChars: formDataJson.length,
            });

            const done = await updatePortalFormComplete(
              facilityKey,
              patientKey,
              facilityFormId,
              formDataJson
            );
            logFormHandlerSubmit("updateFormCompleteResponse", {
              success: done.success,
              reason: done.reason,
            });
            if (!done.success) {
              const detail = done.reason?.trim();
              throw new Error(
                detail
                  ? `Could not mark the form complete: ${detail}`
                  : "Could not mark the form complete. The API returned success: false with an empty reason."
              );
            }

            const portalTaskId = Number(taskId);
            if (Number.isFinite(portalTaskId) && portalTaskId > 0) {
              try {
                await taskRequest.request.updateTaskComplete(
                  facilityKey,
                  patientKey,
                  portalTaskId
                );
                logFormHandlerSubmit("updateTaskComplete", { portalTaskId });
              } catch (e) {
                logFormHandlerSubmit("updateTaskCompleteError", {
                  portalTaskId,
                  message: e instanceof Error ? e.message : String(e),
                });
                throw new Error(
                  e instanceof Error && e.message.trim()
                    ? e.message
                    : "Could not mark this form task complete. Please try again or contact the office."
                );
              }
            }

            const nextRes = await patientRequest.request.gettasks(facilityKey, patientKey);
            const formTasks = ((nextRes?.data as TaskItem[]) ?? []).filter((t) =>
              isFormTask(t.taskType)
            );
            const formTaskSummary = formTasks.map((t) => {
              const inf = parseFacilityFormInfoFromTaskJson(t.info);
              return {
                taskID: t.taskID,
                facilityFormID: inf?.FacilityFormID,
                formName: inf?.FormName,
              };
            });
            logFormHandlerSubmit("getTasksAfterComplete", {
              formTaskCount: formTasks.length,
              tasks: formTaskSummary,
            });

            if (formTasks.length === 0) {
              toast.success("All forms are complete.");
              clearFormHandlerLaunch();
              navigate("/", { replace: true });
              return;
            }

            const nextPayload = buildFormHandlerLaunchPayload(
              formTasks[0],
              formTasks.length
            );
            if (!nextPayload) {
              toast.error("Could not open the next form.");
              clearFormHandlerLaunch();
              navigate("/", { replace: true });
              return;
            }
            const nextInfo = parseFacilityFormInfoFromTaskJson(formTasks[0].info);
            logFormHandlerSubmit("navigateNextForm", {
              nextTaskID: formTasks[0].taskID,
              nextFacilityFormID: nextInfo?.FacilityFormID,
              sameAsCompleted: nextInfo?.FacilityFormID === facilityFormId,
            });
            navigateToFormHandler(navigate, nextPayload, { replace: true });
          } catch (e) {
            const message = userFacingSubmitError(e);
            console.error("[FormHandler] Form submit error:", e);
            logFormHandlerSubmit("submitError", {
              message,
              errorName: e instanceof Error ? e.name : undefined,
              errorString: String(e),
            });
            toast.error(message);
            submittingLock.current = false;
            setPhase("survey");
            try {
              sender.clear(true, true);
            } catch {
              /* ignore */
            }
          }
        };

        m.onComplete.add(onComplete);
        modelRef.current = m;
        setModel(m);
        setPhase("survey");
      } catch (e) {
        if (!cancelled) {
          console.error("Form load error:", e);
          setLoadError(
            e instanceof Error ? e.message : "Could not load this form."
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      disposeModel();
    };
  }, [
    facilityKey,
    patientKey,
    navigate,
    location.key,
    location.state,
    legacySearchKey,
    searchParams,
  ]);

  if (loadError) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <h1 className="text-lg font-bold text-healthsource-dark">Forms</h1>
        <p className="mt-3 text-sm text-healthsource-gray">{loadError}</p>
        <button
          type="button"
          className="mt-6 min-h-11 rounded-md bg-healthsource-red px-4 py-2 text-sm font-bold text-white"
          onClick={() => {
            clearFormHandlerLaunch();
            navigate("/", { replace: true });
          }}
        >
          Back to home
        </button>
      </div>
    );
  }

  if (phase === "loading" || !model) {
    return (
      <div className="form-handler-page mx-auto w-full max-w-[min(100%,8.5in)] py-16 text-center text-sm text-healthsource-gray">
        Loading form…
      </div>
    );
  }

  return (
    <div className="form-handler-page mx-auto w-full max-w-[min(100%,8.5in)] py-4 sm:px-4 sm:py-6">
      <div className="mb-4">
        <h1 className="text-lg font-extrabold text-healthsource-dark">
          {launchUi?.formTitleHint || model.title || "Sign form"}
        </h1>
        {launchUi?.patientLabel ? (
          <p className="mt-1 text-sm text-healthsource-gray">{launchUi.patientLabel}</p>
        ) : null}
        {(launchUi?.formCountHint ?? 1) > 1 ? (
          <p className="mt-1 text-xs text-healthsource-gray">
            You have multiple forms to sign. They will open one after another.
          </p>
        ) : null}
      </div>
      {phase === "submitting" && (
        <div className="mb-4 rounded-md border border-healthsource-lightLavender bg-white px-4 py-3 text-sm text-healthsource-gray">
          Saving your signed form…
        </div>
      )}
      <div className="form-handler-survey-root bg-white sm:rounded-lg sm:border sm:border-healthsource-lightLavender sm:p-4">
        <Survey model={model} />
      </div>
    </div>
  );
}
